// Calendar geometry: the month grid, the week's columns, the window each view reads, and where a meeting
// sits in a day. Pure date arithmetic in local time; no React, no business math (ADR 0003 §4).

export type CalendarViewKind = 'month' | 'week' | 'agenda'
export const CALENDAR_VIEWS: CalendarViewKind[] = ['month', 'week', 'agenda']
export const AGENDA_DAYS = 28
export const MINUTES_PER_DAY = 24 * 60
/** The shortest block a meeting is drawn as, in minutes, so a five-minute check-in is still clickable. */
export const MIN_BLOCK_MINUTES = 20

const pad = (n: number) => String(n).padStart(2, '0')

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}

export function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

/** Local calendar date as YYYY-MM-DD: the key every view groups by and the deep link carries. */
export function dayKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function parseDayKey(key: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(d.getTime()) || dayKey(d) !== key ? null : d
}

/** The week starts on Monday (a business calendar); pass 0 for Sunday. */
export function startOfWeek(d: Date, weekStartsOn = 1): Date {
  const day = startOfDay(d)
  const back = (day.getDay() - weekStartsOn + 7) % 7
  return addDays(day, -back)
}

export function weekDays(anchor: Date, weekStartsOn = 1): Date[] {
  const start = startOfWeek(anchor, weekStartsOn)
  return Array.from({ length: 7 }, (_, i) => addDays(start, i))
}

/** Six rows of seven: the month, padded from the previous month to the next so the grid never jumps. */
export function monthGrid(anchor: Date, weekStartsOn = 1): Date[][] {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1)
  const start = startOfWeek(first, weekStartsOn)
  return Array.from({ length: 6 }, (_, w) => Array.from({ length: 7 }, (_, i) => addDays(start, w * 7 + i)))
}

/** The window a view reads: [from, to) in local time. */
export function rangeFor(view: CalendarViewKind, anchor: Date, weekStartsOn = 1): { from: Date; to: Date } {
  if (view === 'month') {
    const grid = monthGrid(anchor, weekStartsOn)
    return { from: grid[0][0], to: addDays(grid[5][6], 1) }
  }
  if (view === 'week') {
    const from = startOfWeek(anchor, weekStartsOn)
    return { from, to: addDays(from, 7) }
  }
  const from = startOfDay(anchor)
  return { from, to: addDays(from, AGENDA_DAYS) }
}

/** Previous / next: a month (day clamped), a week, or an agenda page. */
export function stepAnchor(view: CalendarViewKind, anchor: Date, delta: -1 | 1): Date {
  if (view === 'month') {
    const target = new Date(anchor.getFullYear(), anchor.getMonth() + delta, 1)
    const last = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
    return new Date(target.getFullYear(), target.getMonth(), Math.min(anchor.getDate(), last))
  }
  return addDays(startOfDay(anchor), delta * (view === 'week' ? 7 : AGENDA_DAYS))
}

const MONTH_LONG = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' })
const MONTH_DAY = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })

export function periodTitle(view: CalendarViewKind, anchor: Date, weekStartsOn = 1): string {
  if (view === 'month') return MONTH_LONG.format(anchor)
  const { from, to } = rangeFor(view, anchor, weekStartsOn)
  const last = addDays(to, -1)
  const sameMonth = from.getMonth() === last.getMonth() && from.getFullYear() === last.getFullYear()
  const end = sameMonth ? String(last.getDate()) : MONTH_DAY.format(last)
  return `${MONTH_DAY.format(from)} – ${end}, ${last.getFullYear()}`
}

/** ?view=week&date=2026-09-14 — the URL wins; the remembered view and today fill the gaps. */
export function parseCalendarParams(params: URLSearchParams, fallbackView: CalendarViewKind, today: Date): { view: CalendarViewKind; anchor: Date } {
  const v = params.get('view')
  const view = (CALENDAR_VIEWS as string[]).includes(v ?? '') ? (v as CalendarViewKind) : fallbackView
  const anchor = parseDayKey(params.get('date') ?? '') ?? startOfDay(today)
  return { view, anchor }
}

export function calendarParams(view: CalendarViewKind, anchor: Date): Record<string, string> {
  return { view, date: dayKey(anchor) }
}

export function isAllDay(startIso: string, endIso: string): boolean {
  const s = new Date(startIso)
  const e = new Date(endIso)
  return s.getHours() === 0 && s.getMinutes() === 0 && e.getTime() - s.getTime() >= 23 * 60 * 60 * 1000
}

export function minutesIntoDay(d: Date): number {
  return d.getHours() * 60 + d.getMinutes()
}

/** Everything whose [start, end) touches the day, in start order. A meeting over midnight shows on both days. */
export function meetingsOnDay<T extends { start: string; end: string }>(items: T[], day: Date): T[] {
  const from = startOfDay(day).getTime()
  const to = addDays(startOfDay(day), 1).getTime()
  return items
    .filter((m) => { const s = new Date(m.start).getTime(); const e = Math.max(new Date(m.end).getTime(), s + 1); return s < to && e > from })
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
}

/** Where a meeting sits within one day column: minutes from midnight and its clipped height. */
export function clipToDay(startIso: string, endIso: string, day: Date): { top: number; height: number } {
  const dayStart = startOfDay(day).getTime()
  const s = Math.max(new Date(startIso).getTime(), dayStart)
  const e = Math.min(new Date(endIso).getTime(), dayStart + MINUTES_PER_DAY * 60_000)
  const top = Math.round((s - dayStart) / 60_000)
  const height = Math.max(MIN_BLOCK_MINUTES, Math.round((e - s) / 60_000))
  return { top, height: Math.min(height, MINUTES_PER_DAY - top) }
}

/** The hours a week grid shows: the working day, stretched to cover anything earlier or later. */
export function hourSpan(blocks: { top: number; height: number }[], first = 7, last = 19): { first: number; last: number } {
  let lo = first
  let hi = last
  for (const b of blocks) {
    lo = Math.min(lo, Math.floor(b.top / 60))
    hi = Math.max(hi, Math.ceil((b.top + b.height) / 60))
  }
  return { first: Math.max(0, lo), last: Math.min(24, hi) }
}

/** Side-by-side lanes for meetings that overlap: each gets a column and the column count of its cluster. */
export function layoutOverlaps(items: { id: string; start: number; end: number }[]): Map<string, { col: number; cols: number }> {
  const out = new Map<string, { col: number; cols: number }>()
  const sorted = [...items].sort((a, b) => a.start - b.start || b.end - a.end)
  let cluster: typeof sorted = []
  let laneEnds: number[] = []
  let clusterEnd = -Infinity
  const flush = () => {
    for (const c of cluster) out.set(c.id, { col: out.get(c.id)!.col, cols: laneEnds.length })
    cluster = []
    laneEnds = []
  }
  for (const it of sorted) {
    if (cluster.length && it.start >= clusterEnd) flush()
    let col = laneEnds.findIndex((end) => end <= it.start)
    if (col === -1) { col = laneEnds.length; laneEnds.push(it.end) } else laneEnds[col] = it.end
    out.set(it.id, { col, cols: 0 })
    cluster.push(it)
    clusterEnd = Math.max(clusterEnd, it.end)
  }
  if (cluster.length) flush()
  return out
}

/** How busy a day is against the busiest in view, as five steps for the month grid's heat. */
export function densityLevel(count: number, max: number): 0 | 1 | 2 | 3 | 4 {
  if (count <= 0 || max <= 0) return 0
  const r = count / max
  return r >= 1 ? 4 : r >= 0.7 ? 3 : r >= 0.4 ? 2 : 1
}

export function groupByDay<T extends { start: string }>(items: T[]): [string, T[]][] {
  const by = new Map<string, T[]>()
  for (const m of [...items].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())) {
    const k = dayKey(new Date(m.start))
    if (!by.has(k)) by.set(k, [])
    by.get(k)!.push(m)
  }
  return [...by.entries()]
}

const TIME = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' })
export function formatTime(iso: string): string {
  return TIME.format(new Date(iso))
}

export function hourLabel(h: number): string {
  return h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`
}

const DAY_LONG = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
export function dayLabel(d: Date, today: Date): string {
  const long = DAY_LONG.format(d)
  if (sameDay(d, today)) return `Today · ${long}`
  if (sameDay(d, addDays(today, 1))) return `Tomorrow · ${long}`
  if (sameDay(d, addDays(today, -1))) return `Yesterday · ${long}`
  return long
}
